import { router } from "@inertiajs/react"
import { ArchiveRestore } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { useOnline } from "@/hooks/use-online"
import type { CandidateRemoval } from "@/types/candidates"

/**
 * OKS-465 — Removed-state banner for the Candidate detail page.
 *
 * Renders when a candidate has been soft-removed from the active
 * pipeline (`removal != null`). Surfaces who removed it + when, and the
 * single Restore affordance (the pipeline transition action bar is
 * suppressed while removed — UX Spec §2). Restore is confirmed via a
 * shadcn AlertDialog (no reason field), mirroring the Remove flow.
 *
 * `restoreUrl` is the BE-supplied `removal_actions.restore_url` — non-null only
 * for permitted viewers (brand-admin tier / OKS admin acting-as). When
 * null, the Restore button is DOM-absent (URL-nullability rule) but the
 * informational banner still shows so every viewer understands the
 * candidate's removed state.
 */
export interface CandidateRemovedBannerProps {
  removal: CandidateRemoval
  restoreUrl: string | null
  handle: string
}

export function CandidateRemovedBanner({
  removal,
  restoreUrl,
  handle,
}: CandidateRemovedBannerProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const removedOn = new Date(removal.at).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
  const removedBy =
    removal.by?.name ?? t("candidates.detail.removed_banner.unknown_actor")

  function submitRestore() {
    if (!restoreUrl || submitting || !online) return
    setSubmitting(true)
    router.post(
      restoreUrl,
      {},
      {
        preserveScroll: true,
        onSuccess: () => setDialogOpen(false),
        onFinish: () => setSubmitting(false),
      },
    )
  }

  return (
    /*
      OKS-958 item 3 — INERT live region (3 of 3 in this item's file scope,
      not on the rendered page). Records a decision;
      changes nothing.

      role="status" implies aria-live="polite", but this banner is
      record-derived: it renders from the `removal` prop, and the parent
      (`pages/Brand/Candidates/Show.tsx`) gates it as `{removal && …}`. An
      Inertia re-render reconciles rather than remounts, and where the region
      IS newly inserted, content arriving simultaneously with its live region
      is generally not announced. Either way nothing reliably fires, so the
      role is inert rather than wrong.

      🔴 Not being changed, and not because it is too fiddly: no instrument in
      this stack can tell us a change was correct. Playwright, Capybara and
      jsdom all read the ATTRIBUTE; none observes a screen reader. Swapping a
      live-region role with no way to detect a mistake trades a harmless no-op
      for an undetectable regression. Blocked on one NVDA/VoiceOver pass,
      which nobody has run.

      ⚠️ `Show.tsx`, which renders this banner, holds three more role="status"
      regions in OKS-958's scope: two inert like this one (appeal-spent,
      self-review advisory) and the offline notice, driven by `useOnline()`,
      which is GENUINELY LIVE and CORRECT.

      🔴 That count of four is scoped to those two files, NOT to the rendered
      page — measured, the page carries EIGHT, and one of the extras is a
      SECOND live `useOnline()` notice inside `candidate-notes-editor.tsx`
      that no OKS-958 document enumerates. Re-enumerate before sweeping: a
      blanket pass would no-op on the inert ones and break the live ones.
      See OKS-958 BRIEF D5.
    */
    <div
      data-testid="candidate-detail-removed-banner"
      role="status"
      className="border-destructive/30 bg-destructive/5 text-destructive flex flex-col gap-3 rounded-md border px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <p className="text-sm font-medium">
        {t("candidates.detail.removed_banner.message", {
          date: removedOn,
          name: removedBy,
        })}
      </p>

      {restoreUrl && (
        <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="candidate-detail-restore"
            aria-label={t("candidates.detail.removed_banner.restore_aria", {
              handle,
            })}
            disabled={submitting || !online}
            onClick={() => setDialogOpen(true)}
            className="sm:flex-shrink-0"
          >
            <ArchiveRestore className="size-4" aria-hidden="true" />
            {t("candidates.detail.removed_banner.restore")}
          </Button>
          <AlertDialogContent data-testid="candidate-detail-restore-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("candidates.detail.removed_banner.restore_dialog_title", {
                  handle,
                })}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t(
                  "candidates.detail.removed_banner.restore_dialog_description",
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={submitting}>
                {t("candidates.detail.removed_banner.restore_dialog_cancel")}
              </AlertDialogCancel>
              {/* OKS-1023 — plain `Button`, not the closing `AlertDialogAction`:
                  the dialog holds while the POST is in flight and closes in
                  `submitRestore`'s `onSuccess`. */}
              <Button
                type="button"
                data-testid="candidate-detail-restore-confirm"
                disabled={submitting || !online}
                onClick={submitRestore}
              >
                {t("candidates.detail.removed_banner.restore_dialog_confirm")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  )
}
